import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { IDialogButtonsContext, SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { ListItem } from 'lib/ui/list/index.js';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { progressHelper } from '../../progress/progress-helper.class.js';

type ExtendedProgressType = UserDb.IUserAssignedProgressType & {
	readonly name: string;
	readonly unit: ProgressTypeDb.ProgressTypeUnit | undefined;
};

type IProgressTableDataRow = {
	readonly id: string;
	readonly name: string;
	readonly unit: ProgressTypeDb.ProgressTypeUnit | undefined;
	readonly buttons: readonly IButton[];
	readonly measurements: number;
	readonly minValue: number;
	readonly maxValue: number;
	readonly avgValue: number;
	readonly simpleTableItems: readonly SimpleTableItem[];
	readonly progressType: UserDb.IUserAssignedProgressType;
};

@Component({
	selector: 'mod-client-progress.',
	templateUrl: './client-progress.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientProgressComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	@ViewChild('newProgressTypeTemplate', { static: true }) newProgressTypeTemplate!: TemplateRef<any>;
	@ViewChild('submitMeasurementsTemplate', { static: true }) submitMeasurementsTemplate!: TemplateRef<any>;

	public readonly assignedClientProgressTypes = computed<readonly UserDb.IUserAssignedProgressType[] | undefined>(
		() => {
			const sharedData = this.sharedData();

			if (!sharedData) {
				return undefined;
			}

			return sharedData.data.assignedProgressTypes;
		}
	);

	public readonly enableEdit = computed<boolean>(() => {
		if (this.authUser()?.userType === 'trainer') {
			return true;
		}
		return false;
	});

	public readonly allProgressTypes = signal<readonly ProgressTypeDb.IProgressTypeItem[] | undefined>(undefined);
	public readonly assignedProgressTypes = signal<readonly ExtendedProgressType[] | undefined>(undefined);

	public readonly measurementButtons = computed<readonly IButton[]>(() => {
		const submitMeasurementsButton: IButton | undefined = this.assignedProgressTypes()?.length
			? {
					icon: 'analytics',
					title: 'Submit measurements',
					buttonType: 'primaryLight',
					click: () => {
						this.dependencies.dialogService.openDialog({
							title: `Submit measurements`,
							subtitle: `Track progress by submitting measurements regularly`,
							size: 'default',
							template: () => this.submitMeasurementsTemplate
						});
					}
			  }
			: undefined;

		if (submitMeasurementsButton) {
			return [submitMeasurementsButton];
		}
		return [];
	});

	public readonly mainButtons = computed<readonly IButton[]>(() => {
		if (!this.enableEdit()) {
			return [];
		}

		return [
			{
				title: 'Assign type',
				buttonType: 'primary',
				buttonSize: 'default',
				click: () => {
					this.openSelectProgressType();
				}
			}
		];
	});

	public readonly availableProgressTypesForAssignment = computed<readonly ProgressTypeDb.IProgressTypeItem[]>(() => {
		const allProgressTypes = this.allProgressTypes();
		const assignedProgressTypes = this.assignedProgressTypes();

		if (!allProgressTypes || !assignedProgressTypes) {
			return [];
		}

		return allProgressTypes.filter((allProgressType) => {
			if (assignedProgressTypes.find((m) => m.progressTypeId === allProgressType.id)) {
				return false;
			}
			return true;
		});
	});

	public readonly assignedProgressTypeListItems = computed<readonly ListItem<ExtendedProgressType>[] | undefined>(
		() => {
			const assignedProgressTypes = this.assignedProgressTypes();

			if (!assignedProgressTypes) {
				return undefined;
			}

			return assignedProgressTypes.map((m) => {
				const item: ListItem<ExtendedProgressType> = {
					data: m,
					id: m.id,
					title: m.name,
					subtitle: m.unit,
					buttons: this.enableEdit()
						? [
								{
									iconOnly: true,
									icon: 'remove',
									title: `Remove`,
									confirm: {
										confirmMode: 'delete',
										text: `Removing the type will cause all progress data to be deleted. Are you sure to continue? This action is not reversible.`
									},
									click: () => {
										this.removeProgressType(m.id);
									}
								}
						  ]
						: []
				};

				return item;
			});
		}
	);

	public readonly tableDataRows = computed<readonly IProgressTableDataRow[] | undefined>(() => {
		const assignedProgressTypes = this.assignedProgressTypes();
		const allProgressTypes = this.allProgressTypes();

		if (!assignedProgressTypes || !allProgressTypes) {
			return undefined;
		}

		return assignedProgressTypes.map((assignedProgressType) => {
			const progressType = allProgressTypes.find((m) => m.id === assignedProgressType.progressTypeId);
			const valuesOverview = progressHelper.calculateMeasurementsOverview(
				assignedProgressType.values,
				this.authUser(),
				progressType?.unit
			);

			const buttons: IButton[] = [];

			if (this.enableEdit()) {
				buttons.push({
					buttonSize: 'default',
					iconOnly: true,
					icon: 'remove',
					title: 'Remove progress type',
					confirm: {
						confirmMode: 'delete',
						text: `Are you sure to unassign the '${progressType?.system.name}' progress type and delete all recorded data? This action is not reversible.`
					},
					click: () => {
						this.removeProgressType(assignedProgressType.id);
					}
				});
			}

			const row: IProgressTableDataRow = {
				progressType: assignedProgressType,
				id: super.getRequiredItemId(progressType),
				name: super.getRequiredItemName(progressType?.system.name),
				unit: valuesOverview.usedUnit,
				minValue: valuesOverview.minValue,
				avgValue: valuesOverview.avgValue,
				maxValue: valuesOverview.maxValue,
				measurements: valuesOverview.measurements,
				buttons: buttons,
				simpleTableItems: [
					{
						title: 'Measurements (#)',
						value: valuesOverview.measurements,
						valueType: 'text'
					},
					{
						title: 'Min. value',
						value: valuesOverview.minValue,
						valueType: 'text'
					},
					{
						title: 'Max. value',
						value: valuesOverview.maxValue,
						valueType: 'text'
					},
					{
						title: 'Average value',
						value: valuesOverview.avgValue,
						valueType: 'text'
					}
				]
			};

			return row;
		});
	});

	constructor() {
		super();

		this.sharedDataInit = () => {
			this.initProgressTypes();
		};
	}

	handleSaveProgress(data: readonly UserDb.IUserAssignedProgressType[]): void {
		this.saveAssignedProgressTypes(data);
		this.dependencies.dialogService.closeAllDialogs();
	}

	handleCreateProgressType(progressType: ProgressTypeDb.IProgressTypeItem): void {
		this.allProgressTypes.update((currentProgressTypes) => {
			return [...(currentProgressTypes ?? []), progressType];
		});

		this.dependencies.dialogService.closeAllDialogs();
		this.assignProgressType(progressType);
	}

	private openCreateCustomProgressTypeDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create type',
			subtitle: 'You may create as many custom types as you need',
			size: 'large',
			template: () => this.newProgressTypeTemplate
		});
	}

	private getSelectProgressTypeDialogButtons(context: IDialogButtonsContext): IButton[] {
		if (!this.enableEdit()) {
			return [];
		}
		return [
			{
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'add',
				title: 'Create type',
				click: () => {
					context.closeDialog();
					this.openCreateCustomProgressTypeDialog();
				}
			}
		];
	}

	private updateAssignedProgressTypes(): void {
		const sharedData = this.sharedData();
		if (!sharedData) {
			return;
		}

		const allProgressTypes: readonly ProgressTypeDb.IProgressTypeItem[] = this.allProgressTypes() ?? [];
		const currentlyAssignedProgressTypes = sharedData.data.assignedProgressTypes ?? [];

		this.assignedProgressTypes.set(
			currentlyAssignedProgressTypes.map<ExtendedProgressType>((assignedProgressType) => {
				const linkedProgressType = allProgressTypes?.find((m) => m.id === assignedProgressType.progressTypeId);

				return {
					...assignedProgressType,
					name: super.getRequiredItemName(linkedProgressType?.system.name),
					unit: linkedProgressType?.unit ?? undefined
				};
			})
		);
	}

	private initProgressTypes(): void {
		if (this.authUser()?.userType === 'trainer') {
			super.subscribeToObservable(
				this.dependencies.httpService
					.get<readonly ProgressTypeDb.IProgressTypeItem[]>((builder) =>
						builder.withCustomApiRoute('progressType', 'getProgressTypes').build()
					)
					.pipe(
						map((response) => {
							this.allProgressTypes.set(response.data);
							this.updateAssignedProgressTypes();
						})
					)
			);
		} else {
			this.allProgressTypes.set(super.getLinkedItemsByType('ProgressType'));
			this.updateAssignedProgressTypes();
		}
	}

	private openSelectProgressType(): void {
		this.dependencies.dialogService.openDialogWithSections({
			dialogId: 'openSelectProgressType',
			size: 'default',
			sections: [
				{
					id: 'allProgressTypes',
					name: 'All types',
					dialog: (builder) => {
						return builder.selectionDialog({
							title: `Select progress type`,
							buttons: (context) => this.getSelectProgressTypeDialogButtons(context),
							items: this.availableProgressTypesForAssignment().map<
								SelectionDialogItem<ProgressTypeDb.IProgressTypeItem>
							>((progressType) => {
								const shownUnit = progressHelper.convertValueToPrefferedFormat({
									preferredUnit: this.authUser()?.preferredUnit,
									unit: progressType.unit,
									value: 0
								}).unit;

								return {
									data: progressType,
									id: progressType.id,
									title: progressType.system.name,
									subtitle: shownUnit
								};
							}),
							select: (item) => {
								this.assignProgressType(item);
							}
						});
					}
				},
				{
					id: 'customProgressTypes',
					name: 'Custom types',
					dialog: (builder) => {
						return builder.selectionDialog({
							title: `Select progress type`,
							buttons: (context) => this.getSelectProgressTypeDialogButtons(context),
							items: this.availableProgressTypesForAssignment()
								.filter((m) => !m.system.isGlobal)
								.map<SelectionDialogItem<ProgressTypeDb.IProgressTypeItem>>((progressType) => {
									const shownUnit = progressHelper.convertValueToPrefferedFormat({
										preferredUnit: this.authUser()?.preferredUnit,
										unit: progressType.unit,
										value: 0
									}).unit;

									return {
										data: progressType,
										id: progressType.id,
										title: progressType.system.name,
										subtitle: shownUnit
									};
								}),
							select: (item) => {
								this.assignProgressType(item);
							}
						});
					}
				}
			]
		});
	}

	private saveAssignedProgressTypes(assignedProgressTypes: readonly UserDb.IUserAssignedProgressType[]): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		sharedData.data.assignedProgressTypes = assignedProgressTypes;
		this.updateAssignedProgressTypes();

		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditProgressRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editProgress')
						.withId(sharedData.data.id)
						.withData({
							assignedProgressTypes: assignedProgressTypes
						})
						.build()
				)
				.pipe(
					map((response) => {
						// update user
						super.updateSharedData({
							data: response.data,
							linkedItems: response.linkedItems
						});

						super.notifySaved('Progress');
					})
				)
		);
	}

	private removeProgressType(progressTypeId: string): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		const filteredProgressTypes = (sharedData.data.assignedProgressTypes ?? []).filter(
			(m) => m.id !== progressTypeId
		);

		this.saveAssignedProgressTypes(filteredProgressTypes);
	}

	private assignProgressType(progressType: ProgressTypeDb.IProgressTypeItem): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		this.saveAssignedProgressTypes([
			...(sharedData.data.assignedProgressTypes ?? []),
			{
				id: guidHelper.shortGuid(),
				progressTypeId: progressType.id,
				values: []
			}
		]);
	}
}
